import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Divider, useAlert, useToast } from 'fasoo-ui-component-next';
import _, { min } from 'lodash';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import { ScanExemptApprovalDetailEntity } from 'src/api/user/scanExemptApprovalApi';
import { CommonProps } from 'src/components/helpers/props';
import usePopup from 'src/hooks/usePopup';
import { getFasooUiComponentLanguage } from 'src/i18n';
import { toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import RemarkPopup from './RemarkPopup';
import { ApprovalUserInfoDto, ShowLineContent, UserDetail } from '.';

import './ScanExemptDetail.scss';

interface ScanExemptDetailProps extends CommonProps {
  scanExemptApproval: ScanExemptApprovalDetailEntity;
  ResetDetail: () => void;
  cancelApprovalChildren?: React.ReactNode;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

export const ScanExemptDetail: React.FC<ScanExemptDetailProps> = ({
  scanExemptApproval,
  ResetDetail,
  cancelApprovalChildren,
  onCloseDetail,
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['menu', 'userApproval']);
  const toast = useToast();
  const { alert } = useAlert();
  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();
  let approvers: ApprovalUserInfoDto[] = [];

  if (scanExemptApproval.completeApprovers.length > 0) {
    const completes = _.sortBy(scanExemptApproval.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (scanExemptApproval.progressApprovers.length > 0) {
    const progresses = _.sortBy(scanExemptApproval.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }
  // 결재선이 다 끝나지 않는 단계
  // if (pcScanExemptApproval.currentStep < pcScanExemptApproval.approvalStep) {
  // let postApprovalSteps: number[] = [];
  // for (let i = 1; i <= pcScanExemptApproval.approvalStep; i++) {
  //   postApprovalSteps = postApprovalSteps.concat(i);
  // }
  // console.log(postApprovalSteps);

  approvers = approvers.concat(
    scanExemptApproval.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );

  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    // console.log('얘가 작성한 remark : ', remark);
    if (scanExemptApproval.progressApprovers.length > 0) {
      const progressApproverInfo = scanExemptApproval.progressApprovers[0];
      const reqApprovalDto: RequestApprovalDto = {
        userId: progressApproverInfo.userId,
        approvalNo: scanExemptApproval.approvalNo,
        approvalMethod: scanExemptApproval.approvalMethod,
        approvalType: scanExemptApproval.approvalType,
        status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
        remark,
        proxyNo: selectedApproval?.progressApprovers[0].proxyNo
          ? selectedApproval.progressApprovers[0].proxyNo
          : null,
      };
      approvalDetailApi
        .approvalOrReject(reqApprovalDto)
        .then((resp) => {
          onCloseDetail(false, true);
        })
        .catch((err) => alert(err, 'error', '', undefined, true));
    } else {
      onCloseDetail(false);
    }

    handleCloseApprovalPopup(popupType);
  };

  return (
    <div className="scan-exempt-detail-root">
      <div
        className={classNames('scan-exempt-main-container', {
          'mg mb-85': isComplete === false,
          // 'mg mb-32': isComplete === true,
        })}
      >
        {/* 신청 유형 아이콘 area */}
        <ApprovalTypeIconArea
          onClose={onCloseDetail}
          type={scanExemptApproval.approvalType}
          status={scanExemptApproval.status}
        />

        <div className="detail-body">
          {/* 반출 기간 */}
          <ShowLineContent
            title={t('userApproval:period.$')}
            content={`${toLocalDateTimeLangStr(
              scanExemptApproval.startDate,
              getFasooUiComponentLanguage()
            )} ~ ${toLocalDateTimeLangStr(
              scanExemptApproval.endDate,
              getFasooUiComponentLanguage()
            )}`}
          />
          {/* 승인 방법  */}
          <ShowLineContent
            classes={classNames('approval-method')}
            title={t('userApproval:approvalMethod.$')}
            content={t(`userApproval:approvalMethod.${scanExemptApproval.approvalMethod}`)}
          />

          {/* 신청자 */}
          <ShowLineContent
            title={t('userApproval:applicant')}
            content={
              <UserDetail
                type="request"
                dateStr={
                  scanExemptApproval.applyDate
                    ? toLocalDateTimeLangStr(
                        scanExemptApproval.applyDate,
                        getFasooUiComponentLanguage()
                      )
                    : ''
                }
                remark={scanExemptApproval.remark}
                userInfo={{
                  userId: scanExemptApproval.userId,
                  userName: scanExemptApproval.userName,
                  positionName: scanExemptApproval.positionName,
                  deptName: scanExemptApproval.deptName,
                  actorIp: scanExemptApproval.actorIp,
                }}
              />
            }
          />
        </div>

        {/* 결재 상세 정보 */}
        <div className="approval-detail-info-title">
          <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
        </div>
        <div className="detail-body">
          <ShowLineContent
            title=""
            content={
              <>
                {approvers.map((approver, idx) => {
                  return (
                    <UserDetail
                      step={approvers.length > 1 ? approver.step : undefined}
                      type="approver"
                      dateStr={
                        approver.approvalDate
                          ? toLocalDateTimeLangStr(
                              approver.approvalDate,
                              getFasooUiComponentLanguage()
                            )
                          : null
                      }
                      remark={approver.approvalRemark ? approver.approvalRemark : null}
                      userInfo={approver}
                      key={`approver-${idx}`}
                    />
                  );
                })}

                {approvers.length <= 1 || <Divider />}
              </>
            }
          />

          {cancelApprovalChildren !== undefined && cancelApprovalChildren}
        </div>
      </div>
      {isComplete === false && (
        <ApprovalDetailFooter
          approvalMethod={scanExemptApproval.approvalMethod}
          handleApprove={() => handleOpenApprovalPopup('approval')}
          handleReject={() => handleOpenApprovalPopup('reject')}
        />
      )}
      {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
        <RemarkPopup
          open={
            openApprovalPopup.opened === true ? openApprovalPopup.opened : openRejectPopup.opened
          }
          entity={scanExemptApproval}
          onSubmit={handleApprovalReject}
          onClose={handleCloseApprovalPopup}
          isReject={openApprovalPopup.opened !== true}
        />
      )}
    </div>
  );
};

export default ScanExemptDetail;
